/**
 * Dados do convite de boleto único no Portal do Consumidor (tarefa #5575).
 *
 * Usado em dois lugares: o `PortalConsumidorGate` (modal que abre sozinho na primeira vez) e
 * o cartão do Início (convite que fica disponível). Cada um tem sua instância; quando um
 * aceita, dispara `EVENTO_ACEITE_ATUALIZADO` e o outro recarrega — sem contexto global só
 * para isso.
 *
 * Tudo aqui passa pela RLS do consumidor: ele só lê o aviso publicado, as próprias UCs, as
 * próprias faturas e a própria confirmação. A gravação do aceite é da função
 * `aceitar_boleto_unico_portal`, que confere tudo de novo no servidor.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { mesesReferenciaDaJanela } from '../lib/boletoUnico/painelAdesao';
import {
  montarConvitePortal,
  type ConvitePortal,
  type UcPortalAceite,
} from '../lib/boletoUnico/aceitePortal';
import type { FaturaDebitoAutomatico } from '../lib/boletoUnico/debitoAutomaticoEquatorial';

export const EVENTO_ACEITE_ATUALIZADO = 'vigor:boleto-unico-aceite-atualizado';

export interface AvisoAceite {
  id: string;
  titulo: string;
  mensagem: string;
}

export interface ResultadoAceite {
  aceitas: { uc_id: number; aceito_em: string }[];
  ignoradas: { uc_id: number; motivo: string }[];
}

const dentroDaVigencia = (inicio: string | null, fim: string | null, agora: Date): boolean =>
  (!inicio || new Date(inicio) <= agora) && (!fim || new Date(fim) >= agora);

export function useConviteBoletoUnico() {
  const { user, profile } = useAuth();
  const ehConsumidor = !!user && profile?.role === 'consumidor';

  const [carregando, setCarregando] = useState(true);
  const [aviso, setAviso] = useState<AvisoAceite | null>(null);
  const [jaVisualizado, setJaVisualizado] = useState(false);
  const [convite, setConvite] = useState<ConvitePortal | null>(null);

  const carregar = useCallback(async () => {
    if (!ehConsumidor) { setCarregando(false); return; }
    setCarregando(true);
    try {
      const agora = new Date();
      const [avisosRes, ucsRes, faturasRes] = await Promise.all([
        // A RLS do consumidor só devolve aviso publicado. Pode haver versão arquivada com o
        // mesmo título: pegamos a mais recente dentro da vigência.
        supabase
          .from('avisos_consumidor')
          .select('id, titulo, mensagem, data_inicio, data_fim, criado_em')
          .eq('categoria', 'aceite')
          .order('criado_em', { ascending: false }),
        supabase
          .from('unidades_consumidoras')
          .select('id, numero_uc, outro_numero_uc, situacao, empresa_parceira_id, boleto_unico, boleto_unico_aceite_em, endereco, logradouro, numero, bairro, cidade'),
        // Só para o alerta de débito automático por UC — janela igual à do painel.
        supabase
          .from('faturas')
          .select('uc_codigo, uc_codigo_norm, mes_referencia, debito_automatico_equatorial, linha_digitavel_equatorial')
          .in('mes_referencia', mesesReferenciaDaJanela(agora, 3))
          .is('excluido_em', null),
      ]);
      if (ucsRes.error) throw ucsRes.error;

      const avisoVigente = ((avisosRes.data || []) as (AvisoAceite & { data_inicio: string | null; data_fim: string | null })[])
        .find(a => dentroDaVigencia(a.data_inicio, a.data_fim, agora)) || null;

      let visto = false;
      if (avisoVigente) {
        const { data: conf } = await supabase
          .from('avisos_consumidor_confirmacoes')
          .select('visualizado_em')
          .eq('aviso_id', avisoVigente.id)
          .eq('profile_id', user!.id)
          .maybeSingle();
        visto = !!conf?.visualizado_em;
      }

      setAviso(avisoVigente ? { id: avisoVigente.id, titulo: avisoVigente.titulo, mensagem: avisoVigente.mensagem } : null);
      setJaVisualizado(visto);
      setConvite(montarConvitePortal(
        (ucsRes.data || []) as UcPortalAceite[],
        // Falha nas faturas não derruba o convite: só não mostra o alerta de débito.
        (faturasRes.error ? [] : faturasRes.data || []) as FaturaDebitoAutomatico[],
        { hoje: agora },
      ));
    } catch (err) {
      // Fail-open, como o resto do Portal: sem convite, o cliente segue usando normalmente.
      console.error('Convite do boleto único:', err);
      setConvite(null);
    } finally {
      setCarregando(false);
    }
  }, [ehConsumidor, user]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const aoAtualizar = () => { carregar(); };
    window.addEventListener(EVENTO_ACEITE_ATUALIZADO, aoAtualizar);
    return () => window.removeEventListener(EVENTO_ACEITE_ATUALIZADO, aoAtualizar);
  }, [carregar]);

  /** Marca que o cliente viu o convite — o modal não abre sozinho de novo. */
  const marcarVisualizado = useCallback(async () => {
    if (!aviso || !user || jaVisualizado) return;
    setJaVisualizado(true);
    const { error } = await supabase
      .from('avisos_consumidor_confirmacoes')
      .insert({ aviso_id: aviso.id, profile_id: user.id, visualizado_em: new Date().toISOString() });
    // Linha já existente (outra aba) também chega aqui como erro de chave: não importa.
    if (error && error.code !== '23505') console.error('Registrar visualização do convite:', error);
  }, [aviso, user, jaVisualizado]);

  /** Grava o aceite das UCs escolhidas. A função devolve o que entrou e o que ficou de fora. */
  const aceitar = useCallback(async (ucIds: number[]): Promise<ResultadoAceite> => {
    if (!aviso) throw new Error('O convite não está mais disponível. Recarregue a página.');
    const { data, error } = await supabase.rpc('aceitar_boleto_unico_portal', {
      p_uc_ids: ucIds,
      p_aviso_id: aviso.id,
    });
    // As mensagens da função já saem em português para o cliente.
    if (error) throw new Error(error.message || 'Não foi possível registrar o aceite agora.');
    window.dispatchEvent(new Event(EVENTO_ACEITE_ATUALIZADO));
    return data as ResultadoAceite;
  }, [aviso]);

  return { carregando, aviso, jaVisualizado, convite, marcarVisualizado, aceitar, recarregar: carregar };
}
